import { useState, useEffect, useMemo } from 'react';
import { CloudType } from '../../types/policies';
import { Badge } from '../../../../ui/common-ui/badge';
import { Input } from '../../../../ui/common-ui/input';
import { Search, ChevronDown, ChevronUp, Link as LinkIcon } from 'lucide-react';
import { fetchAlerts, GroupedAlertsResponse, CloudAlertResponse } from '../../../CloudAlertManagement/services/api';
import { AlertCard } from '../../../CloudAlertManagement/components/alerts/AlertCard';
import { Alert, AlertType, Severity } from '../../../CloudAlertManagement/types/alerts';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../../ui/common-ui/collapsible';

interface PolicyAlertLinkerProps {
  tenantName?: string;
  cloudId?: string;
  selectedCloud: CloudType;
  selectedAlerts: string[];
  onAlertsChange: (alerts: string[]) => void;
  disabled?: boolean;
}

export function PolicyAlertLinker({
  tenantName,
  cloudId,
  selectedCloud,
  selectedAlerts,
  onAlertsChange,
  disabled = false,
}: PolicyAlertLinkerProps) {
  const [isExpanded, setIsExpanded] = useState(selectedAlerts.length > 0);
  const [alerts, setAlerts] = useState<CloudAlertResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const flattenGroupedAlerts = (grouped: GroupedAlertsResponse): CloudAlertResponse[] => {
    const seen = new Set<string>();
    const flatAlerts: CloudAlertResponse[] = [];
    Object.values(grouped || {}).forEach((byType) => {
      Object.values(byType || {}).forEach((alertList) => {
        if (!Array.isArray(alertList)) return;
        alertList.forEach((alert) => {
          const key = alert.alertId || String(alert.id);
          if (!key || seen.has(key)) return;
          seen.add(key);
          flatAlerts.push(alert);
        });
      });
    });
    return flatAlerts;
  };

  const normalizeSeverity = (severity: string): Severity => {
    const normalized = (severity || 'Low').toLowerCase();
    if (normalized === 'critical' || normalized === 'p0') return 'Critical';
    if (normalized === 'high' || normalized === 'p1') return 'High';
    if (normalized === 'medium' || normalized === 'p2') return 'Medium';
    return 'Low';
  };

  const normalizeAlertType = (alertType: string): AlertType => {
    const normalized = (alertType || 'behaviour').toLowerCase();
    if (normalized === 'rule') return 'rule';
    if (normalized === 'sequential') return 'sequential';
    return 'behaviour';
  };

  const normalizeMitre = (mitre: CloudAlertResponse['mitre']) => {
    if (Array.isArray(mitre)) return mitre;
    return Object.entries(mitre || {}).map(([code, value]) => ({
      code,
      key: value,
    }));
  };

  const toAlertCardModel = (alert: CloudAlertResponse): Alert => ({
    id: alert.id,
    alertId: alert.alertId,
    title: alert.title,
    severity: normalizeSeverity(alert.severity),
    type: normalizeAlertType(alert.alertType),
    info: alert.info || {},
    steps: alert.steps || {},
    mitre: normalizeMitre(alert.mitre),
    isEnabled: !alert.isFlagged,
    isCustom: !alert.isDefault,
    cloud: selectedCloud,
    users: alert.users || [],
    resource: alert.resource || [],
    timeWindow: alert.timeWindow || [],
  });

  // Fetch alerts when expanded
  useEffect(() => {
    if (isExpanded && tenantName && cloudId && alerts.length === 0) {
      setIsLoading(true);
      fetchAlerts(tenantName, cloudId, selectedCloud)
        .then((grouped: GroupedAlertsResponse) => {
          setAlerts(flattenGroupedAlerts(grouped));
        })
        .catch(console.error)
        .finally(() => setIsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, tenantName, cloudId, selectedCloud, alerts.length]);

  useEffect(() => {
    if (selectedAlerts.length > 0) {
      setIsExpanded(true);
    }
  }, [selectedAlerts.length]);

  // Reset alerts when cloud/resource changes
  useEffect(() => {
    setAlerts([]);
  }, [tenantName, cloudId, selectedCloud]);

  const toggleAlert = (alertId: string) => {
    if (selectedAlerts.includes(alertId)) {
      onAlertsChange(selectedAlerts.filter(a => a !== alertId));
    } else {
      onAlertsChange([...selectedAlerts, alertId]);
    }
  };

  const filteredAlerts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const selectedOrder = new Map(selectedAlerts.map((id, index) => [String(id), index]));
    return alerts
      .filter(alert => {
        if (!query) return true;
        return alert.title.toLowerCase().includes(query) ||
          (alert.info && Object.values(alert.info).some(v => String(v).toLowerCase().includes(query)));
      })
      .sort((a, b) => {
        const aKey = a.alertId || String(a.id);
        const bKey = b.alertId || String(b.id);
        const aSelected = selectedOrder.has(aKey);
        const bSelected = selectedOrder.has(bKey);
        if (aSelected && bSelected) return selectedOrder.get(aKey)! - selectedOrder.get(bKey)!;
        if (aSelected) return -1;
        if (bSelected) return 1;
        return a.title.localeCompare(b.title);
      });
  }, [alerts, searchQuery, selectedAlerts]);

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <CollapsibleTrigger asChild disabled={disabled}>
        <button
          className={`
            w-full flex items-center justify-between px-4 py-3 rounded-2xl border transition-all duration-300 mt-3
            ${isExpanded 
              ? 'bg-card border-primary/30 shadow-lg shadow-sky-500/5'
              : 'bg-card backdrop-blur-xl shadow-lg shadow-sky-500/5 border-border/50 hover:border-primary/30 hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)]'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-rose-500/10">
              <LinkIcon className="h-4 w-4 text-rose-500" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium">Link Alerts to Remediation Policy</p>
              <p className="text-xs text-muted-foreground">
                {selectedAlerts.length > 0 
                  ? `${selectedAlerts.length} alert${selectedAlerts.length !== 1 ? 's' : ''} linked`
                  : 'Optional: Choose alerts that should trigger this policy'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedAlerts.length > 0 && (
              <Badge variant="secondary" className="bg-rose-500/10 text-rose-600 border-rose-500/20">
                {selectedAlerts.length}
              </Badge>
            )}
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="mt-3">
        <div className="p-4 rounded-2xl border border-border/50 bg-card space-y-4 shadow-lg shadow-sky-500/5">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search alerts by title or parameters..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 text-sm"
            />
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : filteredAlerts.length > 0 ? (
            <div className="max-h-[640px] overflow-y-auto pr-2 scrollbar-thin">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {filteredAlerts.map(alert => {
                const alertKey = alert.alertId || String(alert.id);
                const isSelected = selectedAlerts.includes(alertKey);
                const alertCard = toAlertCardModel(alert);
                
                return (
                  <div
                    key={alertKey}
                    className={`relative animate-fade-in rounded-2xl transition-all duration-300 ${
                      isSelected ? 'shadow-lg shadow-sky-500/10' : ''
                    }`}
                  >
                    <div className={`absolute inset-0 rounded-2xl border-2 pointer-events-none transition-colors ${
                      isSelected ? 'border-primary' : 'border-transparent'
                    }`} />
                    <AlertCard
                      alert={alertCard}
                      onViewDetails={() => toggleAlert(alertKey)}
                      onToggle={() => {}}
                      hideSwitch={true}
                      selectable={true}
                      selected={isSelected}
                      onSelect={() => toggleAlert(alertKey)}
                      hideActions={true}
                    />
                  </div>
                );
              })}
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-sm text-muted-foreground">
              {alerts.length === 0 ? 'No alerts found for this resource.' : 'No alerts match your search.'}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
